import './meetingSettingsDialog.scss';
import { Dialog } from 'primereact/dialog';
import { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useMeetingStore } from '@/stores/meeting';
import { useMeetingSettingsStore } from '@/stores/meetingSettings';
import { MeetingSettings } from '@/types/meeting';
import api from '@/api/api';
import { useUserStore } from '@/stores/user';
import { useSend } from '@/components/SendProvider';
import { ParticipantRole } from '@/consts/meeting';
import { showToast } from '@/components/ToastManager';
import { InputText } from 'primereact/inputtext';
import { useMicrophoneLevel, playTestSound } from '@/hooks/useAudioDevices';
import { useMediaDeviceSelect } from '@livekit/components-react';
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown';

type TabKey = '' | 'meeting' | 'audio';

interface SettingsDialogProps {
  visible: boolean;
  onHide: () => void;
}

type ChangeSettingsForMeeting = {
  settings: MeetingSettings;
};

export default function MeetingSettingsDialog({ visible, onHide }: SettingsDialogProps) {
  const role = useUserStore((state) => state.role);
  const [activeTab, setActiveTab] = useState<TabKey>('');
  const savedSettings = useMeetingSettingsStore((state) => state.settings);
  const setSettingsInStore = useMeetingSettingsStore((state) => state.setSettings);
  const { meeting, setMeetingTitle } = useMeetingStore();
  const [newMeetingTitle, setNewMeetingTitle] = useState('');
  const [prevTitle, setPrevTitle] = useState('');
  const [unsavedSettings, setUnsavedSettings] = useState<MeetingSettings>(savedSettings);
  const { changeSettingsOfMeeting, changeTitleOfMeeting } = useSend();

  useEffect(() => {
    setUnsavedSettings(savedSettings);
  }, [savedSettings]);

  useEffect(() => {
    if (!role) return;
    if (role === ParticipantRole.TEACHER) {
      setActiveTab('meeting');
    } else {
      setActiveTab('audio');
    }
  }, [role]);

  useEffect(() => {
    if (prevTitle) {
      setNewMeetingTitle(prevTitle);
    } else if (meeting) {
      setNewMeetingTitle(meeting.title);
    }
  }, [activeTab, meeting?.title]);

  const onSetNewMeetingTitle = (newMeetingTitle: string) => {
    setNewMeetingTitle(newMeetingTitle);
  };

  const onSetPrevTitle = (meetingTitle: string) => {
    setPrevTitle(meetingTitle);
  };

  const onHideModify = () => {
    onHide();
    setPrevTitle('');
    if (meeting?.title) {
      setNewMeetingTitle(meeting.title);
    }
    setUnsavedSettings(savedSettings);
  };

  const handleOnSave = async () => {
    if (!meeting?.uuid) return;
    if (JSON.stringify(unsavedSettings) !== JSON.stringify(savedSettings)) {
      try {
        await api.updateSettings(meeting?.uuid, {
          auto_exit_with_teacher_from_room: unsavedSettings.autoExitWithTeacherFromRoom,
          can_students_self_transition_to_rooms: unsavedSettings.canStudentsSelfTransitionToRooms,
          can_students_hand_up_in_rooms: unsavedSettings.canStudentsHandUpInRooms
        });
        const message: ChangeSettingsForMeeting = {
          settings: unsavedSettings
        };
        changeSettingsOfMeeting(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
        setSettingsInStore(unsavedSettings);
      } catch (error) {
        console.error('Failed to update check settings:', error);
        showToast('errorToast', 'error', 'Ошибка', 'Не удалось сохранить настройки');
      }
    }

    if (newMeetingTitle !== meeting.title) {
      try {
        await api.editMeetingTitle(meeting?.uuid, newMeetingTitle);
        const message = {
          new_title: newMeetingTitle
        };
        changeTitleOfMeeting(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
        setMeetingTitle(newMeetingTitle);
      } catch (error) {
        console.error('Failed to update meeting name:', error);
        showToast('errorToast', 'error', 'Ошибка', 'Не удалось сохранить настройки');
      }
    }
  };

  return (
    <Dialog
      header="Настройки"
      draggable={false}
      visible={visible}
      onHide={() => {
        onHideModify();
      }}
      modal
      className="settings-dialog"
    >
      <div className="settings-dialog__layout">
        <div className="settings-dialog__layout_sidebar">
          {role === ParticipantRole.TEACHER && (
            <TabButton active={activeTab === 'meeting'} onClick={() => setActiveTab('meeting')}>
              Встреча
            </TabButton>
          )}

          <TabButton active={activeTab === 'audio'} onClick={() => setActiveTab('audio')}>
            Звук
          </TabButton>
        </div>

        <div className="settings-dialog__layout_content">
          <>
            {activeTab === 'meeting' && (
              <MeetingTab
                settings={unsavedSettings}
                setSettings={setUnsavedSettings}
                newMeetingTitle={newMeetingTitle}
                onSetNewMeetingTitle={onSetNewMeetingTitle}
                prevTitle={prevTitle}
                onSetPrevTitle={onSetPrevTitle}
              />
            )}
            {activeTab === 'audio' && <AudioTab />}
          </>
        </div>
      </div>

      <div className="settings-dialog_footer">
        <Button
          onClick={() => {
            onHideModify();
          }}
        >
          Отмена
        </Button>
        <Button
          onClick={() => {
            handleOnSave();
            onHide();
          }}
          disabled={!newMeetingTitle}
        >
          Сохранить
        </Button>
      </div>
    </Dialog>
  );
}

interface TabButton {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
}

function TabButton({ children, active, onClick }: TabButton) {
  return (
    <button
      className={clsx('settings-dialog__layout_sidebar_tab', active && 'active')}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

interface MeetingTab {
  settings: MeetingSettings;
  setSettings: (updater: (prev: MeetingSettings) => MeetingSettings) => void;
  newMeetingTitle: string;
  onSetNewMeetingTitle: (new_title: string) => void;
  prevTitle: string;
  onSetPrevTitle: (title: string) => void;
}

function MeetingTab({
  settings,
  setSettings,
  newMeetingTitle,
  onSetNewMeetingTitle,
  prevTitle,
  onSetPrevTitle
}: MeetingTab) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  // const [prevTitle, setPrevTitle] = useState('');
  const { meeting } = useMeetingStore();

  const handleChange = (name: keyof MeetingSettings, value: boolean) => {
    setSettings((prev: MeetingSettings) => ({
      ...prev,
      [name]: value
    }));
  };

  const editTitleClick = () => {
    setIsEditingTitle(true);
  };

  const confirmEditClick = () => {
    onSetNewMeetingTitle(newMeetingTitle.trim());
    onSetPrevTitle(newMeetingTitle.trim());
    setIsEditingTitle(false);
  };

  const onCancelEditClick = () => {
    if (!meeting) return;
    onSetNewMeetingTitle(prevTitle || meeting.title);
    setIsEditingTitle(false);
  };

  return (
    <div className="settings-dialog__layout_content_current-meeting">
      <div className="settings-dialog__layout_content_current-meeting_title">
        {isEditingTitle ? (
          <div className="title__edit">
            <InputText
              autoFocus
              className="title__edit-input"
              value={newMeetingTitle}
              onChange={(e) => onSetNewMeetingTitle(e.target.value)}
            />
            <Button
              className="title__edit-button title__edit-button_confirm"
              icon="pi pi-check"
              disabled={!newMeetingTitle.length}
              onClick={() => confirmEditClick()}
            />
            <Button
              className="title__edit-button title__edit-button_cancel"
              icon="pi pi-times"
              onClick={onCancelEditClick}
            />
          </div>
        ) : (
          <div>
            <span>{newMeetingTitle}</span>
            <i
              className="pi pi-pencil title__icon_edit"
              title="Изменить название"
              onClick={() => editTitleClick()}
            ></i>
          </div>
        )}
      </div>
      <div className="settings-dialog__layout_content_current-meeting_checkboxes">
        <div className="settings_checkbox_item">
          <Checkbox
            inputId="AutoExitForTeacherFromRoom"
            name="autoExitForTeacherFromRoom"
            onChange={(e) => handleChange('autoExitWithTeacherFromRoom', e.checked || false)}
            checked={settings.autoExitWithTeacherFromRoom}
          />
          <label htmlFor="AutoExitForTeacherFromRoom" className="settings_checkbox_item_label">
            Автоматический выход учеников из комнаты при выходе учителя/ассистента из той же комнаты
          </label>
        </div>
        <div className="settings_checkbox_item">
          <Checkbox
            inputId="CanStudentsSelfTransitionToRooms"
            name="canStudentsSelfTransitionToRooms"
            onChange={(e) => handleChange('canStudentsSelfTransitionToRooms', e.checked || false)}
            checked={settings.canStudentsSelfTransitionToRooms}
          />
          <label
            htmlFor="CanStudentsSelfTransitionToRooms"
            className="settings_checkbox_item_label"
          >
            Разрешить ученикам самостоятельно переходить в комнаты
          </label>
        </div>
        <div className="settings_checkbox_item">
          <Checkbox
            inputId="CanStudentsHandUpInRooms"
            name="canStudentsHandUpInRooms"
            onChange={(e) => handleChange('canStudentsHandUpInRooms', e.checked || false)}
            checked={settings.canStudentsHandUpInRooms}
          />
          <label htmlFor="CanStudentsHandUpInRooms" className="settings_checkbox_item_label">
            Разрешить ученикам поднимать руку в комнатах
          </label>
        </div>
      </div>
    </div>
  );
}

function AudioTab() {
  const {
    devices: microphones,
    activeDeviceId: activeMicId,
    setActiveMediaDevice: setActiveMic
  } = useMediaDeviceSelect({
    kind: 'audioinput'
  });
  const {
    devices: speakerDevices,
    activeDeviceId: activeSpeakerId,
    setActiveMediaDevice: setActiveSpeaker
  } = useMediaDeviceSelect({
    kind: 'audiooutput'
  });

  const { level, start: startMicTest, stop: stopMicTest } = useMicrophoneLevel(activeMicId);
  const [displayVolume, setDisplayVolume] = useState(0);
  const [isTestingMic, setIsTestingMic] = useState(false);

  useEffect(() => {
    if (Math.abs(level - displayVolume) > 2) {
      setDisplayVolume(level);
    }
  }, [level]);

  const toggleMicTest = () => {
    if (isTestingMic) {
      stopMicTest();
      setIsTestingMic(false);
    } else {
      void startMicTest().catch(console.error);
      setIsTestingMic(true);
    }
  };

  const handleSpeakerTest = () => {
    if (activeSpeakerId) {
      void playTestSound(activeSpeakerId).catch(console.error);
    }
  };

  const changeMic = (e: DropdownChangeEvent) => {
    stopMicTest();
    setIsTestingMic(false);
    setActiveMic(e.value);
  };

  return (
    <div className="audio-tab">
      <div className="audio-tab__section">
        <label className="audio-tab__label">Устройство ввода</label>
        <Dropdown
          value={activeMicId}
          options={microphones.map((d) => ({ label: d.label, value: d.deviceId }))}
          onChange={changeMic}
          className="audio-tab__dropdown"
          placeholder="Выберите микрофон"
        />

        <div className="audio-tab__mic-test">
          <Button
            label={isTestingMic ? 'Остановить проверку' : 'Проверить микрофон'}
            onClick={toggleMicTest}
            className="p-button-sm audio-tab__test-button"
          />
          <div className="audio-tab__vu-meter">
            <div className="audio-tab__vu-meter-bar" style={{ width: `${displayVolume}%` }} />
          </div>
        </div>
      </div>

      <div className="audio-tab__section">
        <label className="audio-tab__label">Устройство вывода</label>
        <Dropdown
          value={activeSpeakerId}
          options={speakerDevices.map((d) => ({ label: d.label, value: d.deviceId }))}
          onChange={(e) => void setActiveSpeaker(e.value)}
          className="audio-tab__dropdown"
          placeholder="Выберите динамики"
        />
        <Button
          label="Проверить звук"
          onClick={handleSpeakerTest}
          className="p-button-sm audio-tab__test-button"
          disabled={!activeSpeakerId}
        />
      </div>
    </div>
  );
}
